import { strayLabelsCypherExpr } from './stray'

/**
 * Projection map for one relationship row in a site-bundle collection.
 * Canonicalizes the edge's instruction-site properties to parallel lists
 * (`instruction_indexes` / `opcodes` / `instructions`, index i = one site):
 * prefers the list props written by the deterministic ingest, falls back to
 * agent-written `code_lines` (verbatim instruction text) and finally wraps
 * the legacy scalar `line` / `opcode` / `instruction` shape into
 * single-element lists so pre-list edges still render.
 */
function siteBundleProjection(rel: string, node: string): string {
  return `{
                node: ${node},
                instruction_indexes: coalesce(${rel}.instruction_indexes, CASE WHEN ${rel}.line IS NULL THEN NULL ELSE [${rel}.line] END),
                opcodes: coalesce(${rel}.opcodes, CASE WHEN ${rel}.opcode IS NULL THEN NULL ELSE [${rel}.opcode] END),
                instructions: coalesce(${rel}.instructions, ${rel}.code_lines, CASE WHEN ${rel}.instruction IS NULL THEN NULL ELSE [${rel}.instruction] END)
              }`
}

/**
 * Detail bundle for a single Robotics node.
 *
 * Supports RobotController / RobotArm / RobotJob / RobotIOSignal /
 * RobotCondition / VisionProcess / VisionData so side-table links can land on
 * the same detail pane even though only Controller -> Arm -> Job appear in the
 * left tree. Vision process rows carry, per linked I/O signal, the PLC tags
 * mapped to it and the SCADA tags reached from the signal or those PLC tags.
 *
 * Params: `{ nodeId: string }`.
 *
 * Call/IO collections bind the relationship and return one row per edge with
 * its instruction sites as parallel lists (see {@link siteBundleProjection});
 * the extract layer expands each row back into per-site entries. Controller
 * signal/condition libraries union `DEFINES` with the job path; arm libraries
 * stay job-scoped.
 *
 * Each relationship family lives in its own `CALL { … }` block so chained
 * OPTIONAL MATCH does not Cartesian-product site rows (the same contract
 * as {@link PLANT_PLC_NODE_DETAIL_CYPHER}). Every CALL always emits exactly
 * one aggregated row — label gates sit inside OPTIONAL MATCH WHERE clauses,
 * and null guards use list comprehensions after collect, never upstream
 * WITH … WHERE filters that would wipe the subquery row for other kinds.
 */
export const PLANT_ROBOT_NODE_DETAIL_CYPHER = `
            MATCH (n {id: $nodeId})
              WHERE n:RobotController
                 OR n:RobotArm
                 OR n:RobotJob
                 OR n:RobotIOSignal
                 OR n:RobotCondition
                 OR n:VisionProcess
                 OR n:VisionData
            WITH n, labels(n) AS labelList

            // Controller -> arms[]
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HOSTS]->(ctrlArm:RobotArm)
                WHERE 'RobotController' IN labels(n)
              RETURN collect(DISTINCT ctrlArm) AS controllerArms
            }

            // Controller -> jobs[] (via hosted arms)
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HOSTS]->(ctrlArm:RobotArm)
                WHERE 'RobotController' IN labels(n)
              OPTIONAL MATCH (ctrlArm)-[:HAS_JOB]->(ctrlJob:RobotJob)
              RETURN collect(DISTINCT ctrlJob) AS controllerJobs
            }

            // Controller -> signals[] (job-path ∪ DEFINES)
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HOSTS]->(ctrlArm:RobotArm)
                WHERE 'RobotController' IN labels(n)
              OPTIONAL MATCH (ctrlArm)-[:HAS_JOB]->(ctrlJob:RobotJob)
              OPTIONAL MATCH (ctrlSigJob:RobotIOSignal)-[:READ_BY|WRITTEN_BY]->(ctrlJob)
              WITH n, collect(DISTINCT ctrlSigJob) AS jobPathSigs
              OPTIONAL MATCH (n)-[:DEFINES]->(ctrlSigDef:RobotIOSignal)
                WHERE 'RobotController' IN labels(n)
              WITH n, jobPathSigs, collect(DISTINCT ctrlSigDef) AS defSigs
              RETURN [s IN jobPathSigs + defSigs WHERE s IS NOT NULL] AS controllerSignals
            }

            // Controller -> conditions[] (job-path ∪ DEFINES)
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HOSTS]->(ctrlArm:RobotArm)
                WHERE 'RobotController' IN labels(n)
              OPTIONAL MATCH (ctrlArm)-[:HAS_JOB]->(ctrlJob:RobotJob)
              OPTIONAL MATCH (ctrlCondJob:RobotCondition)-[:USED_BY]->(ctrlJob)
              WITH n, collect(DISTINCT ctrlCondJob) AS jobPathConds
              OPTIONAL MATCH (n)-[:DEFINES]->(ctrlCondDef:RobotCondition)
                WHERE 'RobotController' IN labels(n)
              WITH n, jobPathConds, collect(DISTINCT ctrlCondDef) AS defConds
              RETURN [c IN jobPathConds + defConds WHERE c IS NOT NULL] AS controllerConditions
            }

            // Controller -> vision processes[] / vision data[]
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:DEFINES]->(ctrlVp:VisionProcess)
                WHERE 'RobotController' IN labels(n)
              RETURN collect(DISTINCT ctrlVp) AS controllerVisionProcesses
            }

            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:DEFINES]->(ctrlVd:VisionData)
                WHERE 'RobotController' IN labels(n)
              RETURN collect(DISTINCT ctrlVd) AS controllerVisionData
            }

            // Controller -> controlled equipment[]
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:CONTROLS]->(ctrlEq:Equipment)
                WHERE 'RobotController' IN labels(n)
              RETURN collect(DISTINCT ctrlEq) AS controlledEquipment
            }

            // Controller -> outbound SUPERVISES
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[outS:SUPERVISES]->(sub:RobotController)
                WHERE 'RobotController' IN labels(n)
              WITH collect({
                controller: sub,
                role: CASE WHEN outS IS NULL THEN NULL ELSE outS.role END,
                networkPath: CASE WHEN outS IS NULL THEN NULL ELSE outS.network_path END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.controller IS NOT NULL] AS supervisesRows
            }

            // Controller -> inbound SUPERVISES
            CALL {
              WITH n
              OPTIONAL MATCH (mast:RobotController)-[inS:SUPERVISES]->(n)
                WHERE 'RobotController' IN labels(n)
              WITH collect({
                controller: mast,
                role: CASE WHEN inS IS NULL THEN NULL ELSE inS.role END,
                networkPath: CASE WHEN inS IS NULL THEN NULL ELSE inS.network_path END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.controller IS NOT NULL] AS supervisedByRows
            }

            // Arm -> owning controller
            CALL {
              WITH n
              OPTIONAL MATCH (armController:RobotController)-[:HOSTS]->(n)
                WHERE 'RobotArm' IN labels(n)
              RETURN head(collect(DISTINCT armController)) AS armController
            }

            // Arm -> jobs[]
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HAS_JOB]->(armJob:RobotJob)
                WHERE 'RobotArm' IN labels(n)
              RETURN collect(DISTINCT armJob) AS armJobs
            }

            // Arm -> signals[] (job-scoped)
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HAS_JOB]->(armJob:RobotJob)
                WHERE 'RobotArm' IN labels(n)
              OPTIONAL MATCH (armSig:RobotIOSignal)-[:READ_BY|WRITTEN_BY]->(armJob)
              RETURN collect(DISTINCT armSig) AS armSignals
            }

            // Arm -> conditions[] (job-scoped)
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[:HAS_JOB]->(armJob:RobotJob)
                WHERE 'RobotArm' IN labels(n)
              OPTIONAL MATCH (armCond:RobotCondition)-[:USED_BY]->(armJob)
              RETURN collect(DISTINCT armCond) AS armConditions
            }

            // Job -> owning arm + controller
            CALL {
              WITH n
              OPTIONAL MATCH (jobArm:RobotArm)-[:HAS_JOB]->(n)
                WHERE 'RobotJob' IN labels(n)
              OPTIONAL MATCH (jobController:RobotController)-[:HOSTS]->(jobArm)
              RETURN head(collect(DISTINCT jobArm)) AS jobArm,
                     head(collect(DISTINCT jobController)) AS jobController
            }

            // Job -> outbound CALLS_JOB sites
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[callE:CALLS_JOB]->(outJob:RobotJob)
                WHERE 'RobotJob' IN labels(n)
              WITH collect(${siteBundleProjection('callE', 'outJob')}) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS jobCalls
            }

            // Job -> inbound CALLS_JOB sites
            CALL {
              WITH n
              OPTIONAL MATCH (inJob:RobotJob)-[calledE:CALLS_JOB]->(n)
                WHERE 'RobotJob' IN labels(n)
              WITH collect(${siteBundleProjection('calledE', 'inJob')}) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS jobCalledBy
            }

            // Job -> READ_BY signal sites
            CALL {
              WITH n
              OPTIONAL MATCH (readSig:RobotIOSignal)-[readE:READ_BY]->(n)
                WHERE 'RobotJob' IN labels(n)
              WITH collect(${siteBundleProjection('readE', 'readSig')}) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS jobReadSignals
            }

            // Job -> WRITTEN_BY signal sites
            CALL {
              WITH n
              OPTIONAL MATCH (writtenSig:RobotIOSignal)-[writeE:WRITTEN_BY]->(n)
                WHERE 'RobotJob' IN labels(n)
              WITH collect(${siteBundleProjection('writeE', 'writtenSig')}) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS jobWrittenSignals
            }

            // Job -> USED_BY condition sites
            CALL {
              WITH n
              OPTIONAL MATCH (jobCond:RobotCondition)-[condE:USED_BY]->(n)
                WHERE 'RobotJob' IN labels(n)
              WITH collect(${siteBundleProjection('condE', 'jobCond')}) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS jobConditions
            }

            // Job -> USED_BY vision process / vision data sites
            CALL {
              WITH n
              OPTIONAL MATCH (jobVp:VisionProcess)-[jobVpE:USED_BY]->(n)
                WHERE 'RobotJob' IN labels(n)
              WITH collect(${siteBundleProjection('jobVpE', 'jobVp')}) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS jobVisionProcesses
            }

            CALL {
              WITH n
              OPTIONAL MATCH (jobVd:VisionData)-[jobVdE:USED_BY]->(n)
                WHERE 'RobotJob' IN labels(n)
              WITH collect(${siteBundleProjection('jobVdE', 'jobVd')}) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS jobVisionData
            }

            // Signal -> vision processes that output onto it / that it triggers
            CALL {
              WITH n
              OPTIONAL MATCH (sigVp:VisionProcess)-[sigOutE:OUTPUTS_TO]->(n)
                WHERE 'RobotIOSignal' IN labels(n)
              WITH collect({
                process: sigVp,
                props: CASE WHEN sigOutE IS NULL THEN {} ELSE properties(sigOutE) END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.process IS NOT NULL] AS signalVisionOutputs
            }

            CALL {
              WITH n
              OPTIONAL MATCH (n)-[sigTrigE:TRIGGERS]->(sigTrigVp:VisionProcess)
                WHERE 'RobotIOSignal' IN labels(n)
              WITH collect({
                process: sigTrigVp,
                props: CASE WHEN sigTrigE IS NULL THEN {} ELSE properties(sigTrigE) END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.process IS NOT NULL] AS signalVisionTriggers
            }

            // Vision process / data -> USED_BY job sites
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[visionUseE:USED_BY]->(visionJob:RobotJob)
                WHERE 'VisionProcess' IN labels(n) OR 'VisionData' IN labels(n)
              WITH collect(${siteBundleProjection('visionUseE', 'visionJob')}) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS visionUsedBy
            }

            // Vision process / data -> owning controller
            CALL {
              WITH n
              OPTIONAL MATCH (visionController:RobotController)-[:DEFINES]->(n)
                WHERE 'VisionProcess' IN labels(n) OR 'VisionData' IN labels(n)
              RETURN head(collect(DISTINCT visionController)) AS visionController
            }

            // Vision process -> OUTPUTS_TO signals, each with the PLC tags that
            // map to the signal and the SCADA tags either one maps to
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[vpOutE:OUTPUTS_TO]->(vpOutSig:RobotIOSignal)
                WHERE 'VisionProcess' IN labels(n)
              WITH collect({
                signal: vpOutSig,
                props: CASE WHEN vpOutE IS NULL THEN {} ELSE properties(vpOutE) END,
                plcTags: [(vpOutPt:PLCTag)-[:MAPS_TO_ROBOT_SIGNAL]->(vpOutSig) | vpOutPt],
                scadaRows:
                  [(vpOutSig)-[vpSigMap:MAPS_TO_TAG]->(vpSigTag:Tag) |
                    { tag: vpSigTag, edge: 'MAPS_TO_TAG', sourceId: vpOutSig.id, props: properties(vpSigMap) }] +
                  [(vpPtTag:Tag)<-[vpPtMap:MAPS_TO_TAG]-(vpPt:PLCTag)-[:MAPS_TO_ROBOT_SIGNAL]->(vpOutSig) |
                    { tag: vpPtTag, edge: 'MAPS_TO_TAG', sourceId: vpPt.id, props: properties(vpPtMap) }]
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.signal IS NOT NULL] AS visionOutputRows
            }

            // Vision process <- TRIGGERS signals (same PLC / SCADA reach)
            CALL {
              WITH n
              OPTIONAL MATCH (vpTrigSig:RobotIOSignal)-[vpTrigE:TRIGGERS]->(n)
                WHERE 'VisionProcess' IN labels(n)
              WITH collect({
                signal: vpTrigSig,
                props: CASE WHEN vpTrigE IS NULL THEN {} ELSE properties(vpTrigE) END,
                plcTags: [(vpTrigPt:PLCTag)-[:MAPS_TO_ROBOT_SIGNAL]->(vpTrigSig) | vpTrigPt],
                scadaRows:
                  [(vpTrigSig)-[vpTrigMap:MAPS_TO_TAG]->(vpTrigTag:Tag) |
                    { tag: vpTrigTag, edge: 'MAPS_TO_TAG', sourceId: vpTrigSig.id, props: properties(vpTrigMap) }] +
                  [(vpTrigPtTag:Tag)<-[vpTrigPtMap:MAPS_TO_TAG]-(vpTrigPt2:PLCTag)-[:MAPS_TO_ROBOT_SIGNAL]->(vpTrigSig) |
                    { tag: vpTrigPtTag, edge: 'MAPS_TO_TAG', sourceId: vpTrigPt2.id, props: properties(vpTrigPtMap) }]
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.signal IS NOT NULL] AS visionTriggerRows
            }

            // Vision process -> USES_VISION_DATA; vision data <- processes
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[vpDataE:USES_VISION_DATA]->(vpData:VisionData)
                WHERE 'VisionProcess' IN labels(n)
              WITH collect({
                node: vpData,
                props: CASE WHEN vpDataE IS NULL THEN {} ELSE properties(vpDataE) END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS visionDataRefs
            }

            CALL {
              WITH n
              OPTIONAL MATCH (dataVp:VisionProcess)-[dataVpE:USES_VISION_DATA]->(n)
                WHERE 'VisionData' IN labels(n)
              WITH collect({
                node: dataVp,
                props: CASE WHEN dataVpE IS NULL THEN {} ELSE properties(dataVpE) END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS visionDataUsedByProcesses
            }

            // Signal -> READ_BY job sites
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[readE:READ_BY]->(signalReadJob:RobotJob)
                WHERE 'RobotIOSignal' IN labels(n)
              WITH collect(${siteBundleProjection('readE', 'signalReadJob')}) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS signalReadBy
            }

            // Signal -> WRITTEN_BY job sites
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[writeE:WRITTEN_BY]->(signalWrittenJob:RobotJob)
                WHERE 'RobotIOSignal' IN labels(n)
              WITH collect(${siteBundleProjection('writeE', 'signalWrittenJob')}) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS signalWrittenBy
            }

            // Signal -> owning controller (job path, else DEFINES)
            CALL {
              WITH n, signalReadBy, signalWrittenBy
              WITH n,
                   [r IN signalReadBy + signalWrittenBy | r.node] AS signalJobs
              UNWIND (CASE WHEN size(signalJobs) = 0 THEN [NULL] ELSE signalJobs END) AS signalJob
              OPTIONAL MATCH (signalArm:RobotArm)-[:HAS_JOB]->(signalJob)
                WHERE signalJob IS NOT NULL
              OPTIONAL MATCH (signalController:RobotController)-[:HOSTS]->(signalArm)
              OPTIONAL MATCH (n)<-[:DEFINES]-(defController:RobotController)
                WHERE 'RobotIOSignal' IN labels(n)
              RETURN coalesce(
                       head([c IN collect(DISTINCT signalController) WHERE c IS NOT NULL]),
                       head([c IN collect(DISTINCT defController) WHERE c IS NOT NULL])
                     ) AS signalController
            }

            // Signal -> MAPS_TO_TAG SCADA rows
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[mapEdge:MAPS_TO_TAG]->(st:Tag)
                WHERE 'RobotIOSignal' IN labels(n)
              OPTIONAL MATCH (stTp:TagProvider)-[:PROVIDES]->(st)
              OPTIONAL MATCH (stHost:Compute)-[:HAS_TAG_PROVIDER]->(stTp)
              WITH collect({
                tag: st,
                edge: CASE WHEN mapEdge IS NULL THEN NULL ELSE type(mapEdge) END,
                sourceId: n.id,
                props: CASE WHEN mapEdge IS NULL THEN {} ELSE properties(mapEdge) END,
                providerId: CASE WHEN stTp IS NULL THEN NULL ELSE stTp.id END,
                hostId: CASE WHEN stHost IS NULL THEN NULL ELSE stHost.id END,
                hostUrl: CASE WHEN stHost IS NULL THEN NULL ELSE coalesce(stHost.url, stHost.gateway_url) END,
                project: CASE WHEN stHost IS NULL THEN NULL ELSE coalesce(stHost.web_dev_project, stHost.project) END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.tag IS NOT NULL] AS signalScadaRows
            }

            // Signal detail -> inbound MAPS_TO_ROBOT_SIGNAL
            CALL {
              WITH n
              OPTIONAL MATCH (pt:PLCTag)-[m:MAPS_TO_ROBOT_SIGNAL]->(n)
                WHERE 'RobotIOSignal' IN labels(n)
              WITH collect({
                tag: pt,
                method: CASE WHEN m IS NULL THEN NULL ELSE m.method END,
                confidence: CASE WHEN m IS NULL THEN NULL ELSE m.confidence END,
                register: CASE WHEN m IS NULL THEN NULL ELSE m.register END,
                direction: CASE WHEN m IS NULL THEN NULL ELSE m.direction END
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.tag IS NOT NULL] AS signalPlcMappings
            }

            // Condition -> USED_BY job sites
            CALL {
              WITH n
              OPTIONAL MATCH (n)-[condE:USED_BY]->(conditionJob:RobotJob)
                WHERE 'RobotCondition' IN labels(n)
              WITH collect(${siteBundleProjection('condE', 'conditionJob')}) AS rawRows
              RETURN [r IN rawRows WHERE r.node IS NOT NULL] AS conditionUsedBy
            }

            // Condition -> owning controller
            CALL {
              WITH n, conditionUsedBy
              WITH n,
                   [r IN conditionUsedBy | r.node] AS conditionJobs
              UNWIND (CASE WHEN size(conditionJobs) = 0 THEN [NULL] ELSE conditionJobs END) AS conditionJob
              OPTIONAL MATCH (conditionArm:RobotArm)-[:HAS_JOB]->(conditionJob)
                WHERE conditionJob IS NOT NULL
              OPTIONAL MATCH (conditionController:RobotController)-[:HOSTS]->(conditionArm)
              OPTIONAL MATCH (n)<-[:DEFINES]-(defConditionController:RobotController)
                WHERE 'RobotCondition' IN labels(n)
              RETURN coalesce(
                       head([c IN collect(DISTINCT conditionController) WHERE c IS NOT NULL]),
                       head([c IN collect(DISTINCT defConditionController) WHERE c IS NOT NULL])
                     ) AS conditionController
            }

            // PLCTag mappings for every RobotIOSignal surfaced in tables
            // (controller / arm / job). [NULL] sentinel keeps the one-row
            // CALL guarantee when the union is empty (non-signal kinds, or
            // libraries with no signals).
            CALL {
              WITH n, labelList,
                   controllerSignals, armSignals, jobReadSignals, jobWrittenSignals
              WITH n,
                   (CASE
                     WHEN 'RobotController' IN labelList THEN controllerSignals
                     WHEN 'RobotArm' IN labelList THEN armSignals
                     WHEN 'RobotJob' IN labelList THEN
                       [r IN jobReadSignals + jobWrittenSignals | r.node]
                     ELSE []
                   END) AS tableSigs
              UNWIND (CASE WHEN size(tableSigs) = 0 THEN [NULL] ELSE tableSigs END) AS sig
              OPTIONAL MATCH (pt:PLCTag)-[:MAPS_TO_ROBOT_SIGNAL]->(sig)
                WHERE sig IS NOT NULL
              WITH collect({
                signalId: CASE WHEN sig IS NULL THEN NULL ELSE sig.id END,
                tag: pt
              }) AS rawRows
              RETURN [r IN rawRows WHERE r.tag IS NOT NULL AND r.signalId IS NOT NULL]
                AS robotSignalPlcRows
            }

            RETURN n, labelList,
                   controllerArms, controllerJobs, controllerSignals,
                   controllerConditions, controlledEquipment,
                   controllerVisionProcesses, controllerVisionData,
                   supervisesRows, supervisedByRows,
                   armController, armJobs, armSignals, armConditions,
                   jobArm, jobController, jobCalls, jobCalledBy,
                   jobReadSignals, jobWrittenSignals, jobConditions,
                   jobVisionProcesses, jobVisionData,
                   signalReadBy, signalWrittenBy, signalController,
                   signalScadaRows, signalPlcMappings,
                   signalVisionOutputs, signalVisionTriggers,
                   conditionUsedBy, conditionController,
                   visionUsedBy, visionController, visionOutputRows, visionTriggerRows,
                   visionDataRefs, visionDataUsedByProcesses,
                   robotSignalPlcRows,
                   ${strayLabelsCypherExpr('n', 'labelList')} AS strayLabels
          `
